/**
 * Modal components for Hologram Club. Extracted from HoligramClub.jsx
 * as part of the file-split refactor.
 *
 * Exports:
 *   JoinModal, ReserveModal, WatchModal, Field
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Youtube, Music } from "lucide-react";
import { http, mediaUrl } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import SetlistPlayer from "@/components/SetlistPlayer";
import { ytId } from "@/components/club/Media";
import { Meta } from "@/components/club/Sections";

/* ============================ Field (shared by JoinModal + ReserveModal) ============================ */
export function Field({ id, label, value, onChange, type = "text", required, min, max, testId }) {
  return (
    <div>
      <label htmlFor={id} className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">
        {label}
      </label>
      <input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-sm focus:border-[#00E5FF] focus:outline-none neon-ring transition-colors"
      />
    </div>
  );
}

/* ============================ JOIN MODAL ============================ */
export function JoinModal({ open, onClose, defaultTier }) {
  const [form, setForm] = useState({ name: "", email: "", tier: defaultTier || "spectator" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (defaultTier) setForm((f) => ({ ...f, tier: defaultTier }));
  }, [defaultTier]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post(`/members/signup`, form);
      toast.success(`Welcome to the Club, ${data.name.split(" ")[0]}.`, {
        description: `Tier: ${data.tier.toUpperCase()} — guest list confirmed.`,
      });
      setForm({ name: "", email: "", tier: "spectator" });
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="join-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md card-surface gradient-border p-8">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="join-close">
          <X size={20} />
        </button>
        <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Membership / Apply</div>
        <h3 className="mt-3 font-display font-black tracking-tighter text-3xl">Join The Club.</h3>
        <p className="mt-2 text-white/60 text-sm">We hold the door open 24/7. Give us your coordinates.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field id="name" label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="join-name" />
          <Field id="email" label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="join-email" />
          <div>
            <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">Tier</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {["spectator", "resident", "vip_immortal"].map((t) => (
                <button
                  type="button"
                  key={t}
                  data-testid={`tier-select-${t}`}
                  onClick={() => setForm({ ...form, tier: t })}
                  className={`py-3 font-mono-ui text-[10px] tracking-[0.22em] uppercase border transition-colors ${
                    form.tier === t
                      ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]"
                      : "border-white/15 text-white/70 hover:border-white/40"
                  }`}
                >
                  {t.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="join-submit" className="btn-neon w-full disabled:opacity-60">
            {loading ? "Transmitting..." : "Request Access"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

/* ============================ RESERVE MODAL ============================ */
export function ReserveModal({ open, onClose, shows, preselect, venueId }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", show_id: "", guests: 2, date: "", venue: venueId || "vegas" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { setForm((f) => ({ ...f, venue: venueId || "vegas" })); }, [venueId]);
  useEffect(() => {
    if (preselect) setForm((f) => ({ ...f, show_id: preselect.id }));
    else if (shows?.length && !form.show_id) setForm((f) => ({ ...f, show_id: shows[0].id }));
  }, [preselect, shows]); // eslint-disable-line
  useEffect(() => {
    if (open && user) {
      setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
    }
  }, [open, user]);
  useEffect(() => {
    if (!form.date) {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setForm((f) => ({ ...f, date: d.toISOString().slice(0, 10) }));
    }
  }, []); // eslint-disable-line

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post(`/reservations`, { ...form, guests: Number(form.guests) });
      setSuccess(data);
      toast.success(`Table held — ${data.confirmation_code}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Reservation failed.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  const close = () => { setSuccess(null); onClose(); };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="reserve-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md card-surface gradient-border p-8">
        <button onClick={close} className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="reserve-close">
          <X size={20} />
        </button>

        {success ? (
          <div>
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Confirmed</div>
            <h3 className="mt-3 font-display font-black tracking-tighter text-3xl">See you inside.</h3>
            <p className="mt-2 text-white/60 text-sm">Your table is held. Bring this code to the door.</p>
            <div className="mt-6 border border-[#00E5FF]/40 bg-[#00E5FF]/5 p-5 text-center">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">Confirmation</div>
              <div className="mt-2 font-display font-black tracking-tighter text-3xl">{success.confirmation_code}</div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-2 text-sm font-mono-ui">
              <Meta label="Guests" value={String(success.guests)} />
              <Meta label="Date" value={success.date} />
            </div>
            <button onClick={close} className="btn-neon w-full mt-6" data-testid="reserve-done">Done</button>
          </div>
        ) : (
          <>
            <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[#00E5FF]">Reserve / A Table</div>
            <h3 className="mt-3 font-display font-black tracking-tighter text-3xl">Pick Your Night.</h3>
            <p className="mt-2 text-white/60 text-sm">Select a show, tell us who you are, and we'll hold a table on the floor.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field id="r-name" label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="reserve-name" />
              <Field id="r-email" label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="reserve-email" />
              <div>
                <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/50">Show</label>
                <select data-testid="reserve-show" value={form.show_id} onChange={(e) => setForm({ ...form, show_id: e.target.value })} className="mt-2 w-full bg-[#050505] border border-white/15 text-white p-3 font-mono-ui text-xs tracking-[0.1em] uppercase focus:border-[#00E5FF] focus:outline-none">
                  {shows.map((s) => (
                    <option key={s.id} value={s.id}>{s.artist} — {s.slot}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field id="r-date" label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required testId="reserve-date" />
                <Field id="r-guests" label="Guests" type="number" value={form.guests} onChange={(v) => setForm({ ...form, guests: v })} min={1} max={12} required testId="reserve-guests" />
              </div>
              <button type="submit" disabled={loading} className="btn-neon w-full disabled:opacity-60" data-testid="reserve-submit">
                {loading ? "Holding table..." : "Hold My Table"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}

/* ============================ WATCH (PPV) MODAL ============================ */
export function WatchModal({ open, onClose, show }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(false);
  // Tracks which song in the 5-song setlist is currently playing (kept for API compat).
  const [, setTrackIdx] = useState(0);

  useEffect(() => {
    if (!open || !show) return;
    setAccess(null);
    setTrackIdx(0);
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/ppv/${show.id}/access`);
        setAccess(data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [open, show, user]);

  if (!open || !show) return null;

  const buy = async () => {
    if (!user) { onClose(); navigate("/hologram-club/login"); return; }
    setBuying(true);
    try {
      const { data } = await http.post(`/payments/ppv/checkout`, {
        show_id: show.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PPV checkout failed");
      setBuying(false);
    }
  };
  const ppv = Number(show.ppv_price_usd || 0);
  const hasAccess = !!access?.has_access;

  const AMBIENT_FALLBACK_URL = "/api/uploads/_demo/hologram-ambient.mp3?v=2";
  // Public-friendly: surface the show's own audio_url to ALL visitors (the
  // famous-artist demo scores live under /api/uploads/_demo/score-*.mp3, so
  // there's no IP concern). Logged-in members can also unlock the ambient
  // fallback if their show has no audio_url. PPV gating still applies to
  // the full YouTube setlist + extended experience above.
  const publicAudio = show.audio_url || (hasAccess ? AMBIENT_FALLBACK_URL : null);
  const rawAudio = access?.audio_url || publicAudio;
  // mediaUrl() resolves the path against window.location.origin so the audio
  // element loads same-origin in production (no cross-origin metadata stall).
  const audioSrc = mediaUrl(rawAudio);
  const usingAmbient = hasAccess && (!access?.audio_url || (access.audio_url || "").includes("/api/uploads/_demo/"));

  const charSrc = access?.character_image_url
    ? mediaUrl(access.character_image_url)
    : show.image;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-4" data-testid="watch-modal">
      <div className="absolute inset-0 bg-black/95 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="relative w-full max-w-6xl h-[90vh] max-h-[1000px] overflow-hidden border border-[#00E5FF]/30 bg-black flex flex-col md:flex-row">
        <button onClick={onClose} className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full border border-white/30 bg-black/60 backdrop-blur flex items-center justify-center text-white/80 hover:text-white hover:border-[#FF007F] transition-colors" data-testid="watch-close">
          <X size={18} />
        </button>

        {/* LEFT — hologram stage */}
        <div className="relative flex-1 md:w-3/5 min-h-[280px] overflow-hidden bg-black">
          {/* If a Hedra-generated MP4 exists, play THAT (real AI lip-synced
              singing performer). Otherwise fall back to the static image
              + hologram-projection CSS overlays.

              Note: Headless Playwright Chromium doesn't ship H.264 codec so
              the video appears black in automated tests, but plays perfectly
              in real-user browsers (Chrome, Safari, Firefox, Edge). */}
          {show.hologram_video_url ? (
            <video
              ref={(el) => {
                if (!el) return;
                // The Hedra MP4 has demo-score audio baked in. We mute it
                // and let YouTube/MP3 player below provide the real song —
                // the lip-sync motion still reads as the artist singing.
                el.muted = true;
                el.volume = 0;
              }}
              src={mediaUrl(show.hologram_video_url)}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              onLoadedData={(e) => { try { e.currentTarget.play(); } catch {} }}
              onCanPlay={(e) => { try { e.currentTarget.play(); } catch {} }}
              data-testid="watch-hologram-video"
            />
          ) : (
            <img
              src={charSrc}
              alt={show.artist}
              className="absolute inset-0 w-full h-full object-cover hologram-isolated hologram-projection"
              data-testid="watch-hologram-figure"
            />
          )}
          <div
            className="absolute pointer-events-none"
            data-testid="watch-emitter-pad"
            style={{
              left: "50%", bottom: "8%", width: "44%", height: "5%",
              transform: "translateX(-50%)",
              background: "radial-gradient(ellipse at center, rgba(160,255,255,0.85) 0%, rgba(60,200,255,0.5) 30%, rgba(255,80,170,0.25) 65%, rgba(0,0,0,0) 100%)",
              filter: "blur(3px)", mixBlendMode: "screen",
            }}
          />
          <div className="absolute inset-0 hologram-cyan-veil" />
          <div className="absolute inset-0 hologram-scanlines-overlay" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/15 to-transparent pointer-events-none" />

          {hasAccess && audioSrc && (
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 border border-[#FF007F] bg-black/70 backdrop-blur px-3 py-1.5 font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#FF007F] font-bold">
              <span className="ring-dot" /> {usingAmbient ? "Hologram Score · Live" : "Now Playing · Live"}
            </div>
          )}
          {access?.is_original && (
            <div className="absolute top-5 right-16 inline-flex items-center gap-1 border border-[#FFD166] bg-[#FFD166] text-black px-2.5 py-1 font-mono-ui text-[10px] tracking-[0.32em] uppercase font-bold">
              Original
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="font-mono-ui text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#00E5FF]">{show.slot} · {show.era}</div>
            <div className="mt-2 font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">{show.artist}</div>
            {show.tagline && <p className="mt-3 text-white/65 text-sm md:text-base max-w-md">{show.tagline}</p>}
          </div>
        </div>

        {/* RIGHT — player + meta panel */}
        <div className="md:w-2/5 flex flex-col bg-[#0B0608] border-t md:border-t-0 md:border-l border-white/10 overflow-y-auto">
          <div className="p-6 md:p-8 flex-1">
            {!user ? (
              <div data-testid="watch-public-preview">
                <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
                  <span className="ring-dot" /> Public Preview
                </div>
                <h3 className="mt-2 font-display font-black tracking-tighter text-3xl leading-tight">Hologram Score</h3>
                <p className="mt-2 text-white/60 text-sm">Listen to the demo score below. Log in for the full setlist and 24/7 access.</p>
                {/* Public visitors: show the MP3 player ONLY for originals
                    (which have no YouTube setlist). For famous artists, the
                    YouTube setlist (gated behind login) is the only audio
                    source — we don't want a duplicate demo-score MP3. */}
                {audioSrc && !(Array.isArray(show.setlist) && show.setlist.length > 0) && !show.youtube_url && (
                  <div className="mt-5 border border-[#00E5FF]/40 bg-gradient-to-br from-[#00E5FF]/10 to-[#FF007F]/10 p-4" data-testid="watch-mp3-block-public">
                    <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF] inline-flex items-center gap-2 mb-3">
                      <Music size={14} />
                      <span className="ring-dot" /> Now Playing · Original Track
                    </div>
                    <audio
                      controls
                      autoPlay
                      loop
                      preload="auto"
                      src={audioSrc}
                      className="w-full"
                      style={{ filter: "hue-rotate(170deg) saturate(0.8)" }}
                      data-testid="watch-audio"
                    >
                      Your browser doesn&rsquo;t support audio playback.
                    </audio>
                  </div>
                )}
                <div className="mt-6 border-t border-white/10 pt-5 text-center">
                  <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF007F]" data-testid="watch-login-cta">Members only</div>
                  <p className="mt-2 text-white/60 text-sm">{ppv > 0 ? `One-time pay-per-view: $${ppv.toFixed(2)}` : "Free for all members."}</p>
                  <button onClick={() => { onClose(); navigate("/hologram-club/login"); }} className="btn-neon mt-4 w-full" data-testid="watch-login-btn">
                    Log In
                  </button>
                </div>
              </div>
            ) : loading ? (
              <div className="text-center font-mono-ui text-xs tracking-[0.28em] uppercase text-white/50 py-12">Verifying access…</div>
            ) : hasAccess ? (
              <div data-testid="watch-player">
                <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
                  <span className="ring-dot" />
                  {access.reason === "owner" ? "Owner Access" : access.reason === "subscriber" ? "Subscriber Access" : access.reason === "purchased" ? "Purchased" : "Free"}
                </div>
                <h3 className="mt-2 font-display font-black tracking-tighter text-3xl leading-tight">Original Set</h3>
                {access.is_original && (
                  <p className="mt-2 text-white/60 text-sm">100% original IP — created in the Hologram Club studio.</p>
                )}

                {/* Hedra hologram MP4 (silent, lip-sync visual only) plays
                    above. The real song plays here via YouTube setlist or
                    MP3 — kept identical to pre-Hedra behavior. */}
                {hasAccess && (Array.isArray(show.setlist) ? show.setlist.length > 0 : false) ? (
                  <div className="mt-6 border border-[#FF0000]/40 bg-black p-1" data-testid="watch-yt-embed">
                    <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[#FF0000] inline-flex items-center gap-1.5 px-3 pt-2">
                      <Youtube size={11} /> Now Playing · {show.setlist.length}-Song Setlist
                    </div>
                    <div className="relative w-full aspect-video mt-2">
                      <SetlistPlayer
                        setlist={show.setlist}
                        fallbackUrl={show.youtube_url}
                        muted={false}
                        paused={false}
                        onTrackChange={setTrackIdx}
                        className="absolute inset-0"
                        testId="watch-setlist-player"
                      />
                    </div>
                  </div>
                ) : hasAccess && ytId(show.youtube_url) ? (
                  <div className="mt-6 border border-[#FF0000]/40 bg-black p-1" data-testid="watch-yt-embed">
                    <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[#FF0000] inline-flex items-center gap-1.5 px-3 pt-2">
                      <Youtube size={11} /> Now Playing · YouTube Music
                    </div>
                    <div className="relative w-full aspect-video mt-2">
                      <iframe
                        title={`${show.artist} on YouTube`}
                        src={`https://www.youtube-nocookie.com/embed/${ytId(show.youtube_url)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                        className="absolute inset-0 w-full h-full hologram-isolated"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                ) : null}

                {/* Original artists (Kairo / Zade / Onyx / Nubia) have NO
                    YouTube setlist — their music lives in audio_url. Famous
                    artists have a YouTube setlist that plays the real song
                    and audio_url is just a demo score we don't want playing
                    over the real track. So: show the MP3 player ONLY when
                    there's no YouTube content to play. */}
                {audioSrc && !(Array.isArray(show.setlist) && show.setlist.length > 0) && !ytId(show.youtube_url) && (
                  <div className="mt-3 border border-[#00E5FF]/40 bg-gradient-to-br from-[#00E5FF]/10 to-[#FF007F]/10 p-4" data-testid="watch-mp3-block">
                    <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF] inline-flex items-center gap-2 mb-3">
                      <Music size={12} />
                      <span className="ring-dot" /> Original Track
                    </div>
                    <audio
                      src={audioSrc}
                      controls
                      autoPlay
                      loop
                      preload="auto"
                      className="w-full"
                      style={{ filter: "hue-rotate(170deg) saturate(0.6)" }}
                      data-testid="watch-audio"
                    />
                  </div>
                )}

                {(show.youtube_url || (Array.isArray(show.setlist) && show.setlist.length > 0)) && hasAccess && (
                  <button
                    type="button"
                    data-testid="watch-fullscreen"
                    onClick={() => {
                      const wrap = document.querySelector('[data-testid="watch-yt-embed"]');
                      if (!wrap) return;
                      const req =
                        wrap.requestFullscreen ||
                        wrap.webkitRequestFullscreen ||
                        wrap.mozRequestFullScreen ||
                        wrap.msRequestFullscreen;
                      if (req) req.call(wrap);
                    }}
                    className="mt-5 w-full inline-flex items-center justify-center gap-3 bg-[#FF0000] hover:bg-white hover:text-[#FF0000] text-white font-mono-ui text-[11px] tracking-[0.32em] uppercase font-bold py-4 px-5 transition-colors"
                  >
                    <Youtube size={16} /> Watch {show.artist} Fullscreen
                  </button>
                )}

                {show.character_description && (
                  <div className="mt-6">
                    <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-white/40 mb-2">Character</div>
                    <p className="text-white/70 text-sm leading-relaxed">{show.character_description}</p>
                  </div>
                )}
                {show.bio && (
                  <div className="mt-5">
                    <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-white/40 mb-2">Bio</div>
                    <p className="text-white/70 text-sm leading-relaxed">{show.bio}</p>
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="watch-buy">
                <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FF007F]">Pay-Per-View</div>
                <h3 className="mt-2 font-display font-black tracking-tighter text-3xl">Unlock this set.</h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">
                  Resident & VIP members watch every show free.<br/>One-time access:{" "}
                  <span className="text-[#00E5FF] font-mono-ui font-bold">${ppv.toFixed(2)}</span>
                </p>
                <button onClick={buy} disabled={buying} className="btn-neon w-full mt-6 disabled:opacity-60" data-testid="watch-buy-btn">
                  {buying ? "Redirecting to Stripe…" : `Buy Access · $${ppv.toFixed(2)}`}
                </button>
                <p className="mt-3 text-center font-mono-ui text-[10px] tracking-[0.22em] uppercase text-white/40">
                  Or <a href="#membership" className="text-[#00E5FF] hover:text-white" onClick={onClose}>upgrade to Resident</a>.
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
